import React, {useContext, useEffect, useState, useRef} from "react";
import "./ConnectorOverview.css";
import {FormattedMessage, useIntl} from "react-intl";
import {AuxiliaryToolTip, TableColumn} from "@spg/component_library";
import {ScreenSize} from "../utility/ScreenSize";
import API, {Resolution} from "../../logic/API";
import {Connector} from "../../logic/APITypes";
import {Table} from "../../reusables/Table";
import {CopyToClipboard} from "../../reusables/CopyToClipboard";
import {ToggleStatusButton} from "../../reusables/ToggleStatusButton";
import {generateTopicDescriptionText} from "../../logic/IntlTexts";

type WebSocketState =
  | {
      status: "connecting" | "reconnecting" | "error";
    }
  | {
      status: "open";
      webSocket: WebSocket;
    };
type ConnectorState = Connector & {isToggling: boolean};

/**
 * A page for admins with a tabular overview of all connectors and the ability to start and stop them
 */
function ConnectorOverview() {
  const screenSize = useContext(ScreenSize);
  const intl = useIntl();

  const [fetchedConnectors, setFetchedConnectors] = useState<
    Resolution<Connector[]>
  >({
    success: false,
    unresolved: true,
  });
  const [connectors, setConnectors] = useState<ConnectorState[]>([]);
  const [webSocketState, setWebSocketState] = useState<WebSocketState>({
    status: "connecting",
  });
  const webSocketStateRef = useRef(webSocketState);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const columns: TableColumn[] = [
    {
      key: "id",
      label: <FormattedMessage id="connectorOverview.id" />,
      sortable: true,
      columnCount: {
        "Big Desktop": 1,
        Desktop: 1,
        Tablet: 1,
      },
      collapsibleMobile: true,
    },
    {
      key: "name",
      label: <FormattedMessage id="connectorOverview.name" />,
      sortable: true,
      columnCount: {
        "Big Desktop": 7,
        Desktop: 7,
        Tablet: 3,
        Mobile: 3,
      },
    },
    {
      key: "type",
      label: <FormattedMessage id="connectorOverview.type" />,
      sortable: true,
      columnCount: {
        "Big Desktop": 2,
        Desktop: 2,
        Tablet: 2,
      },
      collapsibleMobile: true,
    },
    {
      key: "health",
      label: <FormattedMessage id="connectorOverview.health" />,
      sortable: true,
      columnCount: {
        "Big Desktop": 1,
        Desktop: 1,
        Tablet: 1,
        Mobile: 1,
      },
    },
    {
      key: "changeStatus",
      label: <FormattedMessage id="connectorOverview.changeStatus" />,
      srOnlyLabel: true,
      columnCount: {
        "Big Desktop": 1,
        Desktop: 1,
        Tablet: 1,
        Mobile: 1,
      },
    },
  ];

  const initializeList = () => {
    API.getConnectors().then((response) => {
      setFetchedConnectors(response);
      setConnectors(
        (response.payload || []).map((connector) => ({
          ...connector,
          isToggling: false,
        })),
      );
    });
  };

  const initializeWebsocket = () => {
    const ws = API.initializeConnectorsWebSocket();
    ws.onopen = () => {
      setWebSocketState({
        status: "open",
        webSocket: ws,
      });

      pingIntervalRef.current = setInterval(() => {
        if (
          webSocketStateRef.current.status == "open" &&
          webSocketStateRef.current.webSocket.readyState === WebSocket.OPEN
        ) {
          ws.send("ping");
        }
      }, 30000);
    };
    ws.onerror = () => {
      setWebSocketState((oldState) => {
        if (oldState.status !== "open") return {status: "error"};
        initializeWebsocket();
        return {status: "reconnecting"};
      });
    };
    ws.onclose = () => {
      setWebSocketState({status: "error"});
    };
    ws.onmessage = (event) => {
      const message = JSON.parse((event as MessageEvent).data);
      if (
        "message_type" in message &&
        message["message_type"] === "STATUS_CHANGED"
      ) {
        setConnectors((oldConnectors) => {
          const newConnectors = [...oldConnectors];
          const index = oldConnectors.findIndex(
            (connector) => connector.id === message.id,
          );
          if (index !== -1)
            newConnectors[index] = {
              ...newConnectors[index],
              health: message.health,
            };
          else initializeList();
          return newConnectors;
        });
      } else initializeList();
    };
  };

  useEffect(() => {
    initializeList();
    initializeWebsocket();
    return () => {
      if (
        webSocketStateRef.current.status === "open" &&
        webSocketStateRef.current.webSocket
      ) {
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        webSocketStateRef.current.webSocket.close();
      }
    };
  }, []);

  useEffect(() => {
    webSocketStateRef.current = webSocketState;
  }, [webSocketState]);

  return (
    <div className="landingPageGrid">
      <div className="connectorOverviewContainer">
        <h1 id="connectorOverviewHeading">
          <FormattedMessage id="connectorOverview.title" />
        </h1>
        {connectors.length === 0 && !fetchedConnectors.unresolved ? (
          <div className="connectorOverviewBlankText">
            <FormattedMessage
              id={
                "connectorOverview." +
                (fetchedConnectors.success ? "blank" : "failed")
              }
            />
          </div>
        ) : (
          <>
            {(webSocketState.status === "reconnecting" ||
              webSocketState.status === "error") && (
              <div className="connectorOverviewBlankText">
                <FormattedMessage
                  id={"connectorOverview.socket." + webSocketState.status}
                />
              </div>
            )}
            <Table
              id={"connectorsTable"}
              data={connectors.map((connector, index) => ({
                id: connector.id,
                cells: {
                  id: {
                    sortingKey: connector.id,
                    value: (
                      <div className="connectorOverviewId">
                        <AuxiliaryToolTip
                          toolTip={connector.id}
                          idBase={"connectorOverviewId" + connector.id}
                          tooltipPosition={"bottom"}
                        >
                          <CopyToClipboard
                            text={connector.id}
                            idBase={"connectorOverviewId" + connector.id}
                            title={intl.formatMessage({
                              id: "connectorOverview.id.copy",
                            })}
                            additionalButtonProps={{
                              buttonSize: {width: 48, height: 48},
                            }}
                          />
                        </AuxiliaryToolTip>
                      </div>
                    ),
                  },
                  name: {
                    content: connector.name,
                    hasToolTip: true,
                    link: connector.monitoring_url
                      ? {
                          to: connector.monitoring_url,
                          target: "_blank",
                          rel: "noreferrer",
                        }
                      : undefined,
                  },
                  type: generateTopicDescriptionText(intl, connector, "METHOD"),
                  health: {
                    sortingKey: generateTopicDescriptionText(
                      intl,
                      connector,
                      "HEALTH",
                    ),
                    value: (
                      <span
                        className={
                          "connectorOverviewStatus" +
                          (connector.isToggling ? " disabled" : "")
                        }
                      >
                        <span>
                          {generateTopicDescriptionText(
                            intl,
                            connector,
                            "HEALTH",
                          )}
                        </span>
                      </span>
                    ),
                  },
                  changeStatus: {
                    sortingKey: "",
                    value: (
                      <ToggleStatusButton
                        topic={connectors[index]}
                        setTopic={(newConnector) => {
                          setConnectors((oldConnectors) => {
                            const newConnectors = [...oldConnectors];
                            newConnectors[index] = newConnector;
                            return newConnectors;
                          });
                        }}
                        buttonSize={{width: 48, height: 48}}
                      />
                    ),
                  },
                },
                expandableContent:
                  screenSize.category === "Mobile" ? (
                    <div className="connectorOverviewExpandContainer">
                      <div>
                        <div>
                          <span className="connectorOverviewExpandLabel">
                            <FormattedMessage id="connectorOverview.type.expand" />
                          </span>
                          {generateTopicDescriptionText(
                            intl,
                            connector,
                            "METHOD",
                          )}
                        </div>
                      </div>
                      <div>
                        <div>
                          <span className="connectorOverviewExpandLabel">
                            <FormattedMessage id="connectorOverview.id.expand" />
                          </span>
                          {connector.id}
                        </div>
                        <div className="connectorOverviewExpandCopy">
                          <CopyToClipboard
                            text={connector.id}
                            idBase={"connectorOverviewId" + connector.id}
                            title={intl.formatMessage({
                              id: "connectorOverview.id.copy",
                            })}
                            additionalButtonProps={{
                              buttonSize: {width: 40, height: 40},
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : undefined,
              }))}
              columns={columns}
              pageSize={12}
              ariaLabelledBy={"connectorOverviewHeading"}
              isLoading={fetchedConnectors.unresolved}
            />
          </>
        )}
      </div>
    </div>
  );
}

export {ConnectorOverview};
